import { HttpClient } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import { Store } from "@ngrx/store";
import { Mutex } from "async-mutex";
import moment from "moment";
import { IMqttMessage } from "ngx-mqtt";
import {
  BehaviorSubject,
  Observable,
  of,
  Subscription,
  throwError,
} from "rxjs";
import {
  debounceTime,
  filter,
  map,
  switchMap,
  tap,
  withLatestFrom,
} from "rxjs/operators";
import { environment } from "src/environments/environment";
import { AddressesForward } from "src/model/class/general classes/addresses-forward";
import { AppReserved } from "src/model/class/general classes/app-reserved";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { GenericApiData } from "src/components/shared-components/class/general-classes/generic-api-data";
import { ApiErrorType } from "src/model/enum/enumApiErrorType";
import { GenericOperationKind } from "src/model/enum/enumGenericOperationKind";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { iAddressesForward } from "src/model/interface/iAddresses-forward";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { iMqttHandling } from "src/model/interface/iMqtt-handling";
import { isUserAuthenticated } from "src/state/selectors/authentication.selectors";
import { AuthenticationService } from "./authentication.service";
import { EventService } from "./event.service";
import { LocalStorageService } from "./local-storage.service";
import { LoggerService } from "./logger.service";
import { MqttClientService } from "./mqtt-client.service";
import { MomentFormats } from "src/model/class/general classes/moment-formats";

@Injectable({
  providedIn: "root",
})
export class AddressesForwardService
  implements OnDestroy, iEvent, iMqttHandling {
  private _logContext: string = "Addresses-forward.service";
  private _addressesForwardStore: GenericApiData = null;
  private _addressesForwardSubject: BehaviorSubject<Array<AddressesForward>> =
    new BehaviorSubject<Array<AddressesForward>>(null);

  private mutex = new Mutex();

  private _isErrorInGetAddressesForwardListAPIRequest: boolean = false;

  private _isErrorNotSupported: boolean = false;

  _eventSubscriptions: Subscription[] = null;
  _mqttAddressSubscriptionsMap: Map<string, Subscription> = null;

  get isErrorInGetAddressesForwardListAPIRequest(): boolean {
    return this._isErrorInGetAddressesForwardListAPIRequest;
  }

  get isErrorNotSupported(): boolean {
    return this._isErrorNotSupported;
  }

  public readonly addressesForwardList =
    this._addressesForwardSubject.asObservable();

  public needToInitialize: boolean = true;

  constructor(
    private _http: HttpClient,
    private _mqttService: MqttClientService,
    private _storageService: LocalStorageService,
    private _eventService: EventService,
    private _loggerService: LoggerService,
    private _store: Store
  ) {
    this._mqttAddressSubscriptionsMap = new Map<string, Subscription>();

    this.subscribeEvents();
    this.subscribeMQTTAddresses();
  }

  ngOnDestroy(): void {
    this._addressesForwardStore = null;

    this._addressesForwardSubject.next(null);
    this._addressesForwardSubject.complete();

    this.unsubscribeEvents();

    this.unsubscribeToMQTTAddresses();
  }

  //#region Subscribe to event-service observable
  subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    this._eventSubscriptions.push(
      this._eventService.OnComeBackOnline.pipe(
        withLatestFrom(this._store.select(isUserAuthenticated)),
        filter((combinedArray: Array<any>) => combinedArray[1]),
        map((combinedArray: Array<any>) => combinedArray[0])
      ).subscribe(() => {
        this.initializedAddressesForwardList();
      })
    );

    /** Operation performed on logout */
    this._eventSubscriptions.push(
      this._eventService.OnMakeLogout.subscribe(async () => {
        this._storageService.remove(AppSettings.KEY_ADDRESSESFORWARD);

        await this.unsubscribeToMQTTAddresses();

        this._addressesForwardStore = null;
        this._addressesForwardSubject.next(null);
        this.needToInitialize = true;
      })
    );

    this._eventSubscriptions.push(
      this._eventService.OnLoginPlantDid.subscribe(() => {
        this.subscribeMQTTAddresses();
      })
    );
  }

  unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }
  //#endregion

  /**
   * Initialize public observable list trasmission: it should be call in
   * the constructor of all component/page
   */
  public Init() {
    if (this.needToInitialize) {
      this.initializedAddressesForwardList();

      this.needToInitialize = false;
    }
  }

  /**
   * Reload service data (es. in case of error)
   */
  public reloadServiceData() {
    this.needToInitialize = true;
    this.Init();
  }

  /**
   * Initilized addressesForward list: first it read from storage (via getFromStorage()
   * function) then if sotrage read failed it tries to retrieve data from API
   */
  private initializedAddressesForwardList() {
    this.GetFromStorage().then((resultReadStorage: GenericApiData) => {
      if (!resultReadStorage) {
        this.GetAddressesForward();
      } else {
        this.GetAddressesForwardByLastModify(resultReadStorage);
      }
    });
  }

  //#region Mqtt-addresses handling
  async subscribeMQTTAddresses() {
    let mqttAddress: string = "";
    let mqttAddressSubcription: Subscription = null;
    let returnedObservable: Observable<IMqttMessage> = null;

    //#region Subscribing to RESERVED_DB_ADDRESSES_FORWARD_CHANGED
    mqttAddress =
      AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
      "_" +
      AppReserved.RESERVED_DB_ADDRESSES_FORWARD_CHANGED;

    returnedObservable = await this._mqttService.subscribeTopic(mqttAddress);

    mqttAddressSubcription = returnedObservable
      .pipe(
        filter(() => this._addressesForwardStore !== null),
        map((mqttMessage: IMqttMessage) => {
          return this._mqttService.parseMessage(mqttMessage);
        }),
        map(async (message: Array<string>) => {
          /** map operator allow to make parallel execution without cancelling
           * event: when this fired multiple times it perform execution without
           * waiting previous execution complete
           */
          if (message.length === 3) {
            const release = await this.mutex.acquire();

            try {
              const operationKind = parseInt(message[0]);
              const idAddressesForward = parseInt(message[1]);
              const clientID = message[2];
              /** Finding operation kind: Insert, Update, Delete */
              if (operationKind === GenericOperationKind.INSERT) {
                const indexAddressesForward =
                  this._addressesForwardStore.data.findIndex(
                    (addressesForward: AddressesForward) =>
                      addressesForward.id === idAddressesForward
                  );
                if (indexAddressesForward < 0) {
                  const insertAddressesForward: AddressesForward =
                    await this.GetAddressesForwardByID(
                      idAddressesForward
                    ).catch((error) => {
                      return null;
                    });

                  if (insertAddressesForward) {
                    this._addressesForwardStore.data.push(
                      insertAddressesForward
                    );
                    this._addressesForwardStore.data =
                      this._addressesForwardStore.data.filter(
                        (addressesForward: AddressesForward) =>
                          addressesForward.id !== null
                      );
                  } else {
                    throw new Error(
                      "OnInsertAddressesForward - Error retrieving addressesForward with id= " +
                      idAddressesForward
                    );
                  }
                }
              } else if (operationKind === GenericOperationKind.UPDATE) {
                const indexUpdateAddressesForward =
                  this._addressesForwardStore.data.findIndex(
                    (addressesForward: AddressesForward) =>
                      addressesForward.id === idAddressesForward
                  );
                if (indexUpdateAddressesForward >= 0) {
                  const updateAddressesForward: AddressesForward =
                    await this.GetAddressesForwardByID(
                      idAddressesForward
                    ).catch((error) => {
                      return null;
                    });
                  if (updateAddressesForward) {
                    this._addressesForwardStore.data[
                      indexUpdateAddressesForward
                    ] = updateAddressesForward;
                    this._addressesForwardStore.data =
                      this._addressesForwardStore.data.filter(
                        (addressesForward: AddressesForward) =>
                          addressesForward.id !== null
                      );
                  } else {
                    throw new Error(
                      "OnUpdateAddressesForward - Error retrieving addresses forward with id= " +
                      idAddressesForward
                    );
                  }
                } else {
                  throw new Error(
                    "OnUpdateAddressesForward addresses forward not found id= " +
                    idAddressesForward
                  );
                }
              } else if (operationKind === GenericOperationKind.DELETE) {
                this._addressesForwardStore.data =
                  this._addressesForwardStore.data.filter(
                    (addressesForward: AddressesForward) =>
                      addressesForward.id !== idAddressesForward
                  );
              }
              return idAddressesForward + "|" + clientID;
            } finally {
              release();
            }
          } else {
            throw new Error(
              "OnAddressesForwardDataChange - Incorrect message format"
            );
          }
        }),
        debounceTime(1000)
      )
      .subscribe(
        (promiseIDChange) => {
          promiseIDChange.then((IDChanged_ClientID) => {
            if (IDChanged_ClientID) {
              const value = IDChanged_ClientID.split("|");
              /** Update store with addresses forward list changed and propagate modify */
              this._addressesForwardStore.lastAPIModified = moment().format(
                MomentFormats.MOMENT_DATETIME_FORMAT
              );
              this._addressesForwardSubject.next(
                this._addressesForwardStore.data
              );
              this._eventService.OnAddressesForwardChange.next(
                IDChanged_ClientID
              );
              this.setToStorage(this._addressesForwardStore);
            }
          });
        },
        (error) => {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            "_mqttService.subscribeTopic",
            "AppReserved.RESERVED_DB_ADDRESSES_FORWARD_CHAGED",
            "Incorrect message format: " + error.message
          );
        }
      );

    await this.checkLocalAddressSubscriptionMap(
      mqttAddress,
      mqttAddressSubcription
    );

    //#endregion
  }

  async checkLocalAddressSubscriptionMap(
    addressToCheck: string,
    addressSubscription: Subscription
  ): Promise<boolean> {
    let result: boolean = false;
    if (this._mqttAddressSubscriptionsMap.has(addressToCheck)) {
      addressSubscription.unsubscribe();

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "checkLocalAddressSubscriptionMap()",
        "Address ALREADY present " + addressToCheck
      );
    } else {
      this._mqttAddressSubscriptionsMap.set(
        addressToCheck,
        addressSubscription
      );

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "checkLocalAddressSubscriptionMap()",
        "NEW address: " + addressToCheck
      );
      result = true;
    }
    return result;
  }

  async unsubscribeToMQTTAddresses(): Promise<void> {
    for (const entry of Array.from(
      this._mqttAddressSubscriptionsMap.entries()
    )) {
      entry[1].unsubscribe();

      this._mqttAddressSubscriptionsMap.delete(entry[0]);
    }
  }
  //#endregion

  //#region API operations
  /**
   *
   * @param idAddressesForward id of the addresses forward to be deleted
   * Delete addresses forward item by id.
   * It cast Observable to Promise (with .toPromise()) and the update private
   * list, storage list and public observable list, otherwise return error.
   * As update and insert operation, store updates are made by mqtt event
   * subscription
   */
  public async DeleteddressesForward(idAddressesForward: number): Promise<any> {
    return this._http
      .delete(
        environment.httpServerRoot +
        AppSettings.API_URL_ADDRESSES_FORWARD +
        "/" +
        idAddressesForward
      )
      .pipe(
        tap((cancel) => {
          const currentAddressesForward: AddressesForward =
            this._addressesForwardStore.data.find(
              (addressesForward: AddressesForward) =>
                addressesForward.id === idAddressesForward
            );

          const mqttAddress =
            AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
            "_" +
            AppReserved.RESERVED_DB_ADDRESSES_FORWARD_CHANGED;

          const mqttMessage =
            GenericOperationKind.DELETE.toString() +
            "|" +
            currentAddressesForward.id.toString();

          /** Fire propagation to all connected client */
          this.publishAddress(mqttAddress, mqttMessage);

          this.SendReservedReloadAddressesForward();
        })
      )
      .toPromise()
      .then(
        (deletedAddressesForward: AddressesForward) => {
          return deletedAddressesForward;
        },
        (error) => {
          throw new Error(error);
        }
      );
  }

  /**
   *
   * @param idAddressesForward id of the addresses forward to be updated
   * @param addressesForwardData addresses forward data to be modified
   *
   * Update addresses forward item by id.
   * As insert and delete operation, store updates are made by mqtt event subscription
   */
  public async UpdateAddressesForward(
    idAddressesForward: number,
    addressesForwardData: iAddressesForward
  ): Promise<AddressesForward> {
    const indexAddressesForward = this._addressesForwardStore.data.findIndex(
      (addressesForward) => idAddressesForward === addressesForward.id
    );

    if (indexAddressesForward >= 0) {
      return this._http
        .put(
          environment.httpServerRoot +
          AppSettings.API_URL_ADDRESSES_FORWARD +
          "/" +
          idAddressesForward,
          addressesForwardData
        )
        .pipe(
          tap((updateAddressesForward: AddressesForward) => {
            const mqttAddress =
              AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
              "_" +
              AppReserved.RESERVED_DB_ADDRESSES_FORWARD_CHANGED;

            const mqttMessage =
              GenericOperationKind.UPDATE.toString() +
              "|" +
              updateAddressesForward.id.toString();

            /** Fire propagation to all connected client */
            this.publishAddress(mqttAddress, mqttMessage);

            this.SendReservedReloadAddressesForward();
          })
        )
        .toPromise()
        .then(
          (updatedAddressesForward: AddressesForward) => {
            return updatedAddressesForward;
          },
          (error) => {
            throw new Error(error);
          }
        );
    } else {
      return Promise.reject();
    }
  }

  /**
   *
   * @param addressesForwardData addresses forward data to be inserted
   *
   * Insert new addresses forward item.
   * As update and delete operation, store updates are made via mqtt event subscription
   */
  public async InsertAddressesForward(
    addressesForwardData: iAddressesForward
  ): Promise<AddressesForward> {
    return this._http
      .post<AddressesForward>(
        environment.httpServerRoot +
        AppSettings.API_URL_ADDRESSES_FORWARD +
        "/",
        addressesForwardData
      )
      .pipe(
        tap((insertAddressesForward: AddressesForward) => {
          const mqttAddress =
            AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
            "_" +
            AppReserved.RESERVED_DB_ADDRESSES_FORWARD_CHANGED;

          const mqttMessage =
            GenericOperationKind.INSERT.toString() +
            "|" +
            insertAddressesForward.id.toString();

          /** Fire propagation to all connected client */
          this.publishAddress(mqttAddress, mqttMessage);

          this.SendReservedReloadAddressesForward();
        })
      )
      .toPromise()
      .then(
        (insertedAddressesForward: AddressesForward) => {
          return insertedAddressesForward;
        },
        (error) => {
          throw new Error(error);
        }
      );
  }

  /**
   *
   * Get addresses forward data from API. If api call fallback in error construction,
   * it set the appriopriate flage "_isErrorInGetAddressesForwardListAPIRequest" to TRUE,
   * it set the store to NULL and emit on the observable a neutral value (IE. empty array).
   */
  private GetAddressesForward(): Subscription {
    return this._http
      .get<GenericApiData>(
        environment.httpServerRoot + AppSettings.API_URL_ADDRESSES_FORWARD
      )
      .subscribe(
        (returnAddressesForwardStore: GenericApiData) => {
          this._isErrorInGetAddressesForwardListAPIRequest = false;

          this._addressesForwardStore = returnAddressesForwardStore;

          this._addressesForwardSubject.next(returnAddressesForwardStore.data);
          this.setToStorage(returnAddressesForwardStore);
        },
        (error) => {
          if (error.status === ApiErrorType.NOT_FOUND) {
            this._addressesForwardSubject.error(error);
          } else {
            this._isErrorInGetAddressesForwardListAPIRequest = true;
            this._addressesForwardStore = null;
            this._addressesForwardSubject.next([]);
          }
        }
      );
  }

  /**
   * Get addresses forward data from API last modify data store.
   * If api call fallback in error constructor, it propagate an observable
   * notification with .error() function
   * If original data store isn't changed, api return nothing (code 200)
   */
  private GetAddressesForwardByLastModify(
    addressesForwardStore: GenericApiData
  ) {
    const queryString: string =
      "?lastModified=" +
      encodeURIComponent(addressesForwardStore.lastAPIModified);

    return this._http
      .get<GenericApiData>(
        environment.httpServerRoot +
        AppSettings.API_URL_ADDRESSES_FORWARD +
        queryString
      )
      .subscribe(
        (returnAddressesForward: GenericApiData) => {
          this._isErrorInGetAddressesForwardListAPIRequest = false;

          if (returnAddressesForward) {
            addressesForwardStore = returnAddressesForward;
            this.setToStorage(addressesForwardStore);
          }

          this._addressesForwardStore = addressesForwardStore;
          this._addressesForwardSubject.next(addressesForwardStore.data);
        },
        (error) => {
          if (error.status === ApiErrorType.NOT_FOUND) {
            this._addressesForwardSubject.error(error);
          } else {
            this._isErrorInGetAddressesForwardListAPIRequest = true;
            this._addressesForwardSubject.next([]);
          }
        }
      );
  }

  /**
   * Get addresses forward data from API.
   */
  private async GetAddressesForwardByID(idAddressesForward: number) {
    const queryString: string = "?id=" + idAddressesForward;

    return this._http
      .get<GenericApiData>(
        environment.httpServerRoot +
        AppSettings.API_URL_ADDRESSES_FORWARD +
        queryString
      )
      .pipe(
        switchMap((returnAddressesForwardList: GenericApiData) => {
          /**
           * Clean API object from unnecessary data and check if data key
           * is ppulated: if length is equal to zero, no addresses forward
           * is finded
           */
          if (returnAddressesForwardList.data.length > 0) {
            const returnAddressesForward: AddressesForward =
              returnAddressesForwardList.data[0];

            return of(returnAddressesForward);
          } else {
            return throwError(new Error());
          }
        })
      )
      .toPromise()
      .then(
        (returnedAddressedForward: AddressesForward) => {
          return returnedAddressedForward;
        },
        (error) => {
          throw new Error();
        }
      );
  }
  //#endregion

  //#region Storage
  protected GetFromStorage(): Promise<object> {
    //get from storage insert in local list of addressesForward
    return this._storageService.get(AppSettings.KEY_ADDRESSESFORWARD).then(
      (addressesForwardStorage: GenericApiData) => {
        if (addressesForwardStorage) {
          return addressesForwardStorage;
        } else {
          return null;
        }
      },
      (error) => {
        return null;
      }
    );
  }

  protected setToStorage(addressesForwardStore: GenericApiData) {
    this._storageService.set(
      AppSettings.KEY_ADDRESSESFORWARD,
      addressesForwardStore
    );
  }
  //#endregion

  //#region mqtt
  publishAddress(mqttAddress: string, value: string) {
    this._mqttService.publishAddress(mqttAddress, value);
  }
  //#endregion

  private SendReservedReloadAddressesForward() {
    const mqttAddress =
      AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
      "_" +
      AppReserved.RESERVED_RELOAD_ADDRESSES_FORWARD;

    this._mqttService.publishAddress(mqttAddress, "1", "REFRESH");
  }
}
